import React from "react";
import { optimized } from "./img";
import { useContent } from "../../ContentContext";

const SectionTitle = ({ children }) => (
    <h3 className="font-display text-4xl sm:text-5xl uppercase font-bold tracking-tight text-[#F5F2EB] leading-[0.95] mb-8">
        {children}
    </h3>
);

const Item = ({ name, desc, emoji }) => (
    <div className="py-5 border-b border-dotted border-[#F5F2EB]/15 last:border-b-0">
        <div className="flex items-center gap-2.5 mb-1.5">
            {emoji && (
                <span className="text-base sm:text-lg flex-shrink-0 leading-none opacity-95">
                    {emoji}
                </span>
            )}
            <div className="font-display text-xl sm:text-2xl uppercase font-bold tracking-tight text-[#F5F2EB] leading-tight">
                {name}
            </div>
        </div>
        {desc && (
            <p className="font-body text-sm sm:text-base text-[#F5F2EB]/70 italic leading-relaxed">
                {desc}
            </p>
        )}
    </div>
);

const PhotoStrip = ({ src, alt, objectFit = "contain" }) => (
    <div className="my-10 -mx-6">
        <div className="relative aspect-square overflow-hidden bg-[#1A1410]">
            <img
                src={optimized(src, { w: 900, q: 80 })}
                alt={alt}
                loading="lazy"
                decoding="async"
                className={`w-full h-full ${objectFit === "cover" ? "object-cover" : "object-contain"}`}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1A1410]/55 via-[#1A1410]/15 to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-5 right-5 flex items-center gap-2.5">
                <div className="h-px w-5 bg-[#D4A437]" />
                <div className="font-body text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F5F2EB] [text-shadow:_0_1px_3px_rgba(0,0,0,0.6)]">
                    {alt}
                </div>
            </div>
        </div>
    </div>
);

// Two square photos side-by-side — breaks up text-heavy menu sections
const PhotoPair = ({ left, right }) => (
    <div className="my-8 grid grid-cols-2 gap-2 -mx-6 px-2">
        {[left, right].map((p, i) => (
            <div
                key={i}
                className="relative aspect-square overflow-hidden bg-[#1A1410] border border-[#D4A437]/15"
            >
                <img
                    src={optimized(p.src, { w: 600, q: 78 })}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/75 via-black/15 to-transparent pointer-events-none" />
                <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
                    <div className="h-px w-3 bg-[#D4A437]" />
                    <div className="font-body text-[9px] uppercase tracking-[0.25em] font-semibold text-[#F5F2EB] [text-shadow:_0_1px_3px_rgba(0,0,0,0.8)] truncate">
                        {p.alt}
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const sandwiches = [
    {
        name: "Brisket Sando",
        emoji: "🐄",
        desc: "Slow-braised brisket, goats cheese, confit garlic, tomato jam, crispy onions & fresh arugula.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/d63l28jm_497C0B9C-E023-433E-A24D-6C6394910C6E.png",
        alt: "Brisket Sando",
    },
    {
        name: "Brisket Philly Cheesesteak",
        emoji: "🐄",
        desc: "12-hour braised brisket, smoky BBQ sauce, sweet peppers & onions, cheddar, mozzarella, crispy onions & arugula.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/qc6ckrxt_IMG_5622.jpg",
        alt: "Brisket Philly Cheesesteak",
    },
    {
        name: "Beef N' Bacon Brisket",
        emoji: "🐄",
        desc: "Braised beef brisket, smoked pork belly, truffle aioli, sweet peppers & onions, crispy onions & arugula.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/azxbd09k_ED224F3C-D642-4382-BB08-C647F05B3389.png",
        alt: "Beef N' Bacon Brisket",
    },
    {
        name: "Farmhouse Griller",
        emoji: "🐓",
        desc: "Breaded chicken, braised pork, smoked pork belly, cheddar, BBQ aioli, crispy onions & arugula.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/9x6b1z01_photo_2026-05-14%2011.48.35.jpeg",
        alt: "Farmhouse Griller",
    },
    {
        name: "Brandied Apple Cubano",
        emoji: "🐖",
        desc: "Braised pork shoulder, brandied apples, smoked pork belly, cheddar, crispy onions, honey mustard, dill pickles & fresh dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/tv8673ph_IMG_5054.jpg",
        alt: "Brandied Apple Cubano",
    },
    {
        name: "Pulled Pork Hoagie",
        emoji: "🐖",
        desc: "15-hour braised pork, bold BBQ sauce, cheddar, apple slaw, crispy onions & fresh dill.",
    },
    {
        name: "Butcher Jumbo Sausage",
        emoji: "🐖",
        desc: "Wild garlic & pepper sausage with sauerkraut, onions, pickles, grainy Dijon, honey mustard & fresh dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/e4mrc1ch_IMG_5715.JPG",
        alt: "Butcher Jumbo Sausage",
    },
    {
        name: "Stampede Dog",
        emoji: "🐖",
        desc: "Classic jumbo hot dog topped with ketchup, mustard, onions & pickles.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/69r51vdd_photo_2026-05-14%2011.48.06.jpeg",
        alt: "Stampede Dog",
    },
    {
        name: "Crispy Chicken Crunch",
        emoji: "🐓",
        desc: "Double-breaded chicken thigh, maple glaze, sriracha aioli, apple slaw & dill pickles.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/dn334i6f_IMG_5061.JPG",
        alt: "Crispy Chicken Crunch",
    },
    {
        name: "Buttery Buffalo Chicken",
        emoji: "🐓🌶️",
        desc: "Double-breaded chicken thigh tossed in Frank's & sriracha butter, topped with iceberg lettuce, tomato & double garlic mayo.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/uxm4dtai_IMG_5923.JPG",
        alt: "Buttery Buffalo Chicken",
    },
    {
        name: "Pesto Chicken Club",
        emoji: "🐓🐖",
        desc: "Marinated chicken thigh, basil pesto, smoked pork belly, cheddar, iceberg, tomato & pea shoots.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/chu6ygz3_IMG_5056.JPG",
        alt: "Pesto Chicken Club",
    },
    {
        name: "East Coast Lobster Roll",
        emoji: "🦞",
        desc: "REAL lobster & crab with tarragon, lemon zest, crispy potato, dill butter & pea shoots.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/m935o9iu_photo_2026-05-14%2011.48.14.jpeg",
        alt: "East Coast Lobster Roll",
    },
    {
        name: "Shrimp Po' Boy",
        emoji: "🦐",
        desc: "Eight panko-breaded shrimp, crisp iceberg, beefsteak tomato, brined pickles & fresh dill, finished with sweet Thai ranch on a garlic-toasted roll.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/gvb9dw64_IMG_5975.jpeg",
        alt: "Shrimp Po' Boy",
    },
    {
        name: "Garden Stack",
        emoji: "🌱",
        desc: "Grilled portobello, onion, peppers, zucchini & eggplant with roasted garlic goat cheese & pesto. (Goat cheese or vegan cheese available)",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/k0tuna96_IMG_7249.jpeg",
        alt: "Garden Stack",
    },
];

const burgerUpgrades = [
    {
        name: "Portobello & Swiss",
        emoji: "🍄",
        desc: "Signature brisket-chuck patty topped with balsamic-grilled portobello mushrooms, melted Swiss cheese & roasted garlic mayo.",
    },
    {
        name: "Jalapeño Crunch",
        emoji: "🌶️",
        desc: "Brisket-chuck smash patty loaded with crispy fried onions, pickled jalapeños & cool buttermilk ranch.",
    },
    {
        name: "Tomato Jam & Goat Cheese",
        emoji: "🥫",
        desc: "House-made brisket-chuck patty, creamy chèvre, sweet & savory tomato jam & baby arugula.",
    },
];

const loaded = [
    {
        name: "Dillish",
        emoji: "🍟",
        desc: "Fresh cut fries or crispy tots, smoked pork belly, hollandaise, cheddar, mozza, paprika & fresh dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/wmgrvjc0_2F0C847C-FEB5-4354-BD93-35C7F385B11B.PNG",
        alt: "Dillish Loaded Tots",
    },
    {
        name: "Dill Pickle Lovers",
        emoji: "🍟",
        desc: "Fresh cut fries or crispy tots, hollandaise, dill pickles, cheddar, paprika & fresh dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/4v3a9m0h_IMG_4956.JPG",
        alt: "Dill Pickle Lovers Loaded Fries",
    },
    {
        name: "Classic Poutine",
        emoji: "🍟",
        desc: "Fresh cut fries, rich gravy & Quebec cheese curds.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/jgvwubm8_IMG_5065.JPG",
        alt: "Classic Poutine",
    },
    {
        name: "Chili Cheese",
        emoji: "🌶️",
        desc: "Fresh cut fries or crispy tots, brisket chuck, 8-bean chili, cheddar, double garlic mayo, bold BBQ & dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/d7n72og3_IMG_5605.JPG",
        alt: "Chili Cheese Loaded Fries",
    },
    {
        name: "Chicken Caesar",
        emoji: "🐓",
        desc: "Fresh cut fries or crispy tots, seasoned popcorn chicken, house-made Caesar dressing, smoked pork belly, cheddar, paprika & iceberg lettuce.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/8s2ugk7k_IMG_5979.jpeg",
        alt: "Chicken Caesar Loaded Fries",
    },
    {
        name: "Vegonator",
        emoji: "🌱",
        desc: "Fresh cut fries or crispy tots, vegan gravy, crispy chickpeas, vegan cheese & fresh dill.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/gh174sox_E589B043-C811-4846-BE09-F821A2F7087B.png",
        alt: "Vegonator Loaded Fries",
    },
];

// (sandwichPairs removed — each sandwich now carries its own matching photo)

const burgerSizes = [
    { name: "Single", weight: "6oz", note: "one patty" },
    { name: "Standard", weight: "12oz", note: "double patty" },
    { name: "Heavyweight", weight: "18oz", note: "triple patty" },
    { name: "Challenge", weight: "24oz", note: "quad patty", subnote: "Not For The Weak 🔥" },
];

// (loadedGallery removed — each loaded item now carries its own matching photo)

export const Menu = () => {
    const [active, setActive] = React.useState(null);
    const topRef = React.useRef(null);
    const { categories: ctxCategories, menuItems: ctxItems, content } = useContent();

    const openCategory = (cat) => {
        setActive(cat);
        requestAnimationFrame(() => {
            if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const goBack = () => {
        setActive(null);
        requestAnimationFrame(() => {
            if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    // Build category cards from CMS data; fall back to hard-coded defaults if API not loaded yet.
    const fallbackCategories = [
        {
            id: "sandwiches",
            label: "Sandwiches",
            sub: "13 hand-built sandos",
            src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/e6pfm7pm_IMG_7249.jpeg",
            pos: "center 50%",
        },
        {
            id: "smash-burgers",
            label: "Smash Burgers",
            sub: "Brisket-chuck blend · 6oz → 24oz",
            src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/nlontx6c_IMG_7248.jpeg",
            pos: "center 50%",
        },
        {
            id: "loaded",
            label: "Loaded Fries & Tots",
            sub: "House specialty · stacked high",
            src: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/cochpde3_IMG_7262.jpeg",
            pos: "center 50%",
        },
    ];

    const categories =
        ctxCategories && ctxCategories.length > 0
            ? ctxCategories.map((c) => ({
                  id: c.slug,
                  _id: c.id,
                  label: c.label,
                  sub: c.sub,
                  src: c.photo,
                  pos: c.pos || "center 50%",
              }))
            : fallbackCategories;

    // Items by category slug (uses CMS data if present; otherwise legacy hard-coded arrays)
    const itemsBySlug = (slug, fallback) => {
        if (!ctxCategories || ctxCategories.length === 0) return fallback;
        const cat = ctxCategories.find((c) => c.slug === slug);
        if (!cat) return fallback;
        const items = (ctxItems || [])
            .filter((i) => i.category_id === cat.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        return items.length > 0 ? items : fallback;
    };

    // Side-choice / house rule / combo callouts — CMS overrides defaults
    const sideChoice = content?.callouts?.side_choice || {
        eyebrow: "All Sandwiches Come With",
        title: "Fresh Cut Fries · Apple Slaw · or Potato Salad",
        sub: "+ Gluten-Free Bun Available",
    };
    const houseRule = content?.callouts?.house_rule || {
        eyebrow: "House Rule",
        title: "Turn Any Sandwich Into Loaded Fries or Tots — Same Price.",
    };
    const goLoaded = content?.callouts?.go_loaded || {
        eyebrow: "Make It A Combo",
        title: "Go Loaded",
        price: "+$8",
        body: "Add Loaded Fries or Crispy Tots on the side with any sandwich.",
        photo: "https://customer-assets.emergentagent.com/job_local-strake/artifacts/zx36f8x3_IMG_5979.jpeg",
    };
    const sizes = (content?.burger_sizes && content.burger_sizes.length > 0) ? content.burger_sizes : burgerSizes;

    const renderSandwiches = () => {
        const items = itemsBySlug("sandwiches", sandwiches);
        return (
            <div data-testid="menu-cat-sandwiches">
                <SectionTitle>{categories.find((c) => c.id === "sandwiches")?.label || "Sandwiches"}</SectionTitle>
                {items.map((it) => (
                    <React.Fragment key={it.id || it.name}>
                        <Item name={it.name} desc={it.desc} emoji={it.emoji} />
                        {it.photo && <PhotoStrip src={it.photo} alt={it.alt || it.name} />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const burgerCat = categories.find((c) => c.id === "smash-burgers");
    const renderSmashBurgers = () => (
        <div data-testid="menu-cat-smash-burgers">
            <SectionTitle>{burgerCat?.label || "Smash Burgers"}</SectionTitle>

            <p className="font-body text-sm sm:text-base text-[#F5F2EB]/75 italic leading-relaxed mb-8">
                {content?.callouts?.smash_burger_intro ||
                    "Signature brisket-chuck patty, sweet mustard, smoky roasted garlic mayo, onions & crisp dill pickles."}
            </p>

            <PhotoStrip
                src={burgerCat?.src || "https://customer-assets.emergentagent.com/job_local-strake/artifacts/f53w0ume_att.XqJQpAckzmDcI1QitpEaFmXOof4n920uxPiKeT2wOOU.jpg"}
                alt="Signature Smash Burger"
            />

            {/* Size matrix */}
            <div className="border-t border-b border-[#D4A437]/30 divide-y divide-[#F5F2EB]/10 mb-10">
                {sizes.map((b) => (
                    <div key={b.name} className="py-4">
                        <div className="flex items-baseline gap-3">
                            <div className="font-display uppercase text-xl sm:text-2xl font-bold tracking-tight text-[#F5F2EB] flex-1 min-w-0">
                                {b.name}
                            </div>
                            <span className="font-display uppercase text-base sm:text-lg text-[#D4A437] font-bold w-[56px] text-right tabular-nums">
                                {b.weight}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EB]/60 italic font-body w-[88px] text-left">
                                {b.note}
                            </span>
                        </div>
                        {b.subnote && (
                            <div className="mt-1.5 text-right">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C65A2C] font-body">
                                    {b.subnote}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="mb-10 text-center">
                <div className="inline-block border-2 border-[#D4A437] px-5 py-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4A437] font-semibold font-body">
                        + Upgrade Your Burger · $3
                    </div>
                </div>
            </div>

            <div>
                {itemsBySlug("smash-burgers", burgerUpgrades).map((it) => (
                    <Item key={it.id || it.name} name={it.name} desc={it.desc} emoji={it.emoji} />
                ))}
            </div>

            {/* Combo upsell — Go Loaded */}
            <div className="mt-14 -mx-6" data-testid="combo-upsell">
                <div className="relative w-full aspect-square overflow-hidden bg-[#1A1410]">
                    <img
                        src={optimized(goLoaded.photo, { w: 1000, q: 82 })}
                        alt="Smash burger and loaded tots combo"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: "center 60%" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410] via-[#1A1410]/25 to-[#1A1410]/10" />
                </div>

                <div className="px-6 pt-3 pb-2 text-center">
                    <div className="flex items-center gap-3 justify-center mb-4">
                        <div className="h-px w-6 bg-[#D4A437]" />
                        <div className="font-body text-[10px] uppercase tracking-[0.4em] font-semibold text-[#D4A437]">
                            {goLoaded.eyebrow}
                        </div>
                        <div className="h-px w-6 bg-[#D4A437]" />
                    </div>
                    <div className="flex items-baseline justify-center gap-3">
                        <h3 className="font-vintage uppercase text-[#F5F2EB] text-[44px] sm:text-[56px] leading-[0.9] tracking-tight [text-shadow:_2px_2px_0_rgba(212,164,55,0.25)]">
                            {goLoaded.title}
                        </h3>
                        <span className="font-vintage uppercase text-[#D4A437] text-[26px] sm:text-[32px] leading-none">
                            {goLoaded.price}
                        </span>
                    </div>
                    <p className="mt-4 font-body text-sm sm:text-base italic text-[#F5F2EB]/75 leading-relaxed max-w-[280px] sm:max-w-sm mx-auto">
                        {goLoaded.body}
                    </p>
                </div>
            </div>
        </div>
    );

    const renderLoaded = () => (
        <div data-testid="menu-cat-loaded">
            {/* Premium vintage smokehouse menu-board title */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-[2px] w-8 bg-[#D4A437]" />
                    <div className="font-stencil text-[#D4A437] text-[10px] uppercase tracking-[0.45em] font-normal">
                        ★ House Specialty ★
                    </div>
                    <div className="h-[2px] flex-grow bg-[#D4A437]/30" />
                </div>

                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="font-vintage text-[32px] sm:text-[42px] uppercase font-normal leading-[0.95] tracking-tight text-[#F5F2EB] [text-shadow:_2px_2px_0_rgba(198,90,44,0.3)]">
                        Loaded Fries
                    </h3>
                    <span className="font-vintage text-[26px] sm:text-[34px] text-[#D4A437]/60 leading-none">
                        /
                    </span>
                    <h3 className="font-vintage text-[20px] sm:text-[26px] uppercase font-normal leading-[0.95] tracking-tight text-[#D4A437]">
                        Loaded Crispy Tots
                    </h3>
                </div>

                <div className="mt-4 flex items-center gap-2">
                    <div className="h-[2px] w-5 bg-[#C65A2C]" />
                    <p className="font-body text-[10px] uppercase tracking-[0.3em] font-semibold text-[#F5F2EB]/60">
                        Handcrafted Daily · Made Fresh
                    </p>
                </div>
            </div>

            {/* Highlight banner */}
            <div className="bg-[#C65A2C] text-[#1A1410] py-5 px-5 mb-8 relative">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1 font-body">
                    ★ {houseRule.eyebrow}
                </div>
                <div className="font-display text-xl sm:text-2xl uppercase font-bold leading-tight tracking-tight">
                    {houseRule.title}
                </div>
            </div>

            {/* Items list — each item carries its own matching photo */}
            <div className="mt-2">
                {itemsBySlug("loaded", loaded).map((it) => (
                    <React.Fragment key={it.id || it.name}>
                        <Item name={it.name} desc={it.desc} emoji={it.emoji} />
                        {it.photo && <PhotoStrip src={it.photo} alt={it.alt || it.name} />}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    const activeCat = categories.find((c) => c.id === active);

    return (
        <section
            data-testid="menu-section"
            className="bg-[#1A1410] text-[#F5F2EB] py-12 px-6 relative overflow-hidden"
        >
            {/* Subtle vignette */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(212,164,55,0.06),transparent_60%)]" />

            <div ref={topRef} className="relative">
                {active === null ? (
                    /* ──────── LANDING: header + side notice + 3 category cards ──────── */
                    <div data-testid="menu-landing">
                        <div className="mb-6">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4A437] font-semibold mb-3 font-body">
                                — Strake Menu Board
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl uppercase font-bold leading-[0.95] tracking-tight text-[#F5F2EB]">
                                The Full <span className="italic font-normal text-[#D4A437]">Spread.</span>
                            </h2>
                        </div>

                        {/* Sandwich side-choice notice */}
                        <div className="mb-8 py-4 text-center border-y border-[#D4A437]/25">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4A437] mb-1.5 font-body">
                                ★ {sideChoice.eyebrow} ★
                            </div>
                            <div className="font-display text-base sm:text-lg uppercase tracking-tight font-medium text-[#F5F2EB]">
                                {sideChoice.title}
                            </div>
                            <div className="mt-1.5 text-[10px] uppercase tracking-[0.25em] text-[#F5F2EB]/60 font-body italic">
                                {sideChoice.sub}
                            </div>
                        </div>

                        <div className="mb-4 flex items-center gap-3">
                            <div className="h-px flex-grow bg-[#D4A437]/40" />
                            <div className="font-body text-[10px] uppercase tracking-[0.4em] font-semibold text-[#D4A437]">
                                Choose Your Category
                            </div>
                            <div className="h-px flex-grow bg-[#D4A437]/40" />
                        </div>

                        <div className="-mx-6 flex flex-col gap-3" data-testid="category-cards">
                            {categories.map((c) => (
                                <a
                                    key={c.id}
                                    href={`#${c.id}`}
                                    data-testid={`cat-${c.id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openCategory(c.id);
                                    }}
                                    className="group relative block w-full h-[210px] sm:h-[260px] overflow-hidden active:opacity-95 transition-opacity"
                                >
                                    <img
                                        src={optimized(c.src, { w: 1100, q: 82 })}
                                        alt={c.label}
                                        loading="lazy"
                                        decoding="async"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03] group-active:scale-[1.02]"
                                        style={{ objectPosition: c.pos }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/25 to-black/5" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4A437] via-[#D4A437]/60 to-transparent" />
                                    <div className="absolute inset-0 flex items-end px-6 pb-6">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-display text-4xl sm:text-5xl uppercase font-bold leading-[0.9] tracking-tight text-[#F5F2EB] [text-shadow:_0_2px_10px_rgba(0,0,0,0.7)]">
                                                {c.label}
                                            </h3>
                                            <p className="mt-2 font-body text-[11px] sm:text-xs uppercase tracking-[0.25em] font-medium text-[#F5F2EB]/85 [text-shadow:_0_1px_4px_rgba(0,0,0,0.7)]">
                                                {c.sub}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 ml-3 mb-1 flex flex-col items-end gap-1">
                                            <div className="font-display text-4xl sm:text-5xl text-[#D4A437] leading-none [text-shadow:_0_2px_8px_rgba(0,0,0,0.75)] transition-transform duration-300 group-active:translate-x-1">
                                                →
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>

                        {/* Footer flourish */}
                        <div className="mt-14 pt-6 border-t border-[#D4A437]/30 text-center">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4A437] font-body mb-2">
                                ✦ ✦ ✦
                            </div>
                            <div className="font-display uppercase text-sm tracking-[0.3em] text-[#F5F2EB]/70 font-body">
                                Handcrafted Daily in Collingwood
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ──────── CATEGORY VIEW: back link + one category only ──────── */
                    <div data-testid={`menu-view-${active}`}>
                        {/* Back to categories */}
                        <a
                            href="#menu"
                            data-testid="back-to-categories"
                            onClick={(e) => {
                                e.preventDefault();
                                goBack();
                            }}
                            className="inline-flex items-center gap-2 mb-6 group active:opacity-80 transition-opacity"
                        >
                            <span className="font-display text-2xl text-[#D4A437] leading-none transition-transform duration-300 group-active:-translate-x-1">
                                ←
                            </span>
                            <span className="font-body text-[11px] uppercase tracking-[0.35em] font-semibold text-[#F5F2EB] border-b border-[#D4A437]/50 pb-0.5">
                                Back To Menu Categories
                            </span>
                        </a>

                        {/* Category eyebrow + breadcrumb-style label */}
                        <div className="mb-6">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4A437] font-semibold font-body">
                                — Strake Menu · {activeCat?.label}
                            </div>
                        </div>

                        {/* Side-choice notice persists on sandwiches view since it's directly relevant */}
                        {active === "sandwiches" && (
                            <div className="mb-8 py-4 text-center border-y border-[#D4A437]/25">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4A437] mb-1.5 font-body">
                                    ★ {sideChoice.eyebrow} ★
                                </div>
                                <div className="font-display text-base sm:text-lg uppercase tracking-tight font-medium text-[#F5F2EB]">
                                    {sideChoice.title}
                                </div>
                                <div className="mt-1.5 text-[10px] uppercase tracking-[0.25em] text-[#F5F2EB]/60 font-body italic">
                                    {sideChoice.sub}
                                </div>
                            </div>
                        )}

                        {active === "sandwiches" && renderSandwiches()}
                        {active === "smash-burgers" && renderSmashBurgers()}
                        {active === "loaded" && renderLoaded()}

                        {/* Back to categories — bottom CTA */}
                        <div className="mt-14 pt-6 border-t border-[#D4A437]/30 text-center">
                            <a
                                href="#menu"
                                data-testid="back-to-categories-bottom"
                                onClick={(e) => {
                                    e.preventDefault();
                                    goBack();
                                }}
                                className="inline-flex items-center gap-2 group active:opacity-80 transition-opacity"
                            >
                                <span className="font-display text-2xl text-[#D4A437] leading-none transition-transform duration-300 group-active:-translate-x-1">
                                    ←
                                </span>
                                <span className="font-body text-[11px] uppercase tracking-[0.35em] font-semibold text-[#F5F2EB] border-b border-[#D4A437]/50 pb-0.5">
                                    Back To Menu Categories
                                </span>
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
